import type {
  FlatfinderData,
  FlatfinderEstateInput,
  FlatfinderPerspective,
} from 'realitiq-utils/src/flatfinder';
import { env } from '@/constants/env';
import { customFetch } from '@/lib/fetch-interceptor';

import { i18n } from '@/lib/i18n';

export class FlatfinderRequestError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'FlatfinderRequestError';
  }
}

export async function flatfinderRequest<T>(
  path: string,
  method = 'GET',
  body?: unknown
): Promise<T> {
  const response = await customFetch(`${env.API_URL}${path}`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const message =
      response.status === 409
        ? i18n.t('flatfinderWorkspaceConflict')
        : response.status === 400 || response.status === 422
          ? i18n.t('flatfinderWorkspaceInvalid')
          : response.status === 401 || response.status === 403
            ? i18n.t('flatfinderWorkspaceForbidden')
            : response.status === 404
              ? i18n.t('flatfinderWorkspaceNotFound')
              : i18n.t('flatfinderWorkspaceSaveFailed');
    throw new FlatfinderRequestError(response.status, message);
  }
  return response.json() as Promise<T>;
}

export const getFlatfinder = (projectId: string) =>
  flatfinderRequest<FlatfinderData>(`/projects/${projectId}/flatfinder`);
export const initializeFlatfinder = (projectId: string) =>
  flatfinderRequest<FlatfinderData>(`/projects/${projectId}/flatfinder`, 'POST');
export const saveFlatfinderLayout = (
  projectId: string,
  revision: number,
  perspectives: FlatfinderPerspective[]
) =>
  flatfinderRequest<FlatfinderData>(`/projects/${projectId}/flatfinder/layout`, 'PUT', {
    revision,
    perspectives,
  });
export const saveFlatfinderSettings = (
  projectId: string,
  revision: number,
  status: 'draft' | 'published',
  contactLink: string | null
) =>
  flatfinderRequest<FlatfinderData>(`/projects/${projectId}/flatfinder/settings`, 'PUT', {
    revision,
    status,
    contactLink,
  });
export const createFlatfinderEstate = (
  projectId: string,
  regionId: string,
  revision: number,
  estate: FlatfinderEstateInput,
  requestId: string
) =>
  flatfinderRequest<FlatfinderData>(
    `/projects/${projectId}/flatfinder/regions/${regionId}/estate`,
    'POST',
    { revision, estate, requestId }
  );
export const updateFlatfinderEstate = (
  projectId: string,
  estateId: string,
  estate: FlatfinderEstateInput
) =>
  flatfinderRequest<FlatfinderData>(
    `/projects/${projectId}/flatfinder/estates/${estateId}`,
    'PUT',
    estate
  );
